import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import { getEstimateNumberValueBasedOnSettings } from "@Actions/estimation";
import { useSelector } from "react-redux";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import type { RootState } from "src/reducers";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";

type CurrencyDetails = EstimateCurrency;

type BoqItemDetails = {
  amount?: number;
  additionalAmount?: number | string;
  manualQuantity?: number | string;
  quantityFromTakeoffs?: number;
  rate?: number;
};

/**
 * Args object passed by numeric inputs. The component forwards this opaque payload
 * to its parent (EstimationDetails) which extracts `.value` / `.target.value`.
 * Matches the NumericTextBox `onChange` arg shape.
 */
type UpdateItemDetailsArgs = {
  value: number | null;
  previousValue?: number | null;
  event?: Event;
  target: { value: string; name?: string };
};

type BoqItemAmountDetailsProps = {
  itemCurrentDetails: BoqItemDetails;
  currencyDetails: CurrencyDetails;
  updateItemDetails: (args: UpdateItemDetailsArgs, field?: string) => void;
  isActionPermission: boolean;
};

const BoqItemAmountDetails = ({
  itemCurrentDetails,
  currencyDetails,
  updateItemDetails,
  isActionPermission,
}: BoqItemAmountDetailsProps) => {
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const { t } = useTranslation();
  const currencySymbol = currencyDetails.selectedSymbol?.name ?? "";

  const getQuantity = () => {
    const val =
      parseFloat(String(itemCurrentDetails.manualQuantity ?? 0)) + Number(itemCurrentDetails.quantityFromTakeoffs ?? 0);
    return getEstimateNumberValueBasedOnSettings(val, estimateData, "quantity");
  };

  const getTotalAmount = () => {
    const val =
      Number(itemCurrentDetails.amount ?? 0) + (parseFloat(String(itemCurrentDetails.additionalAmount ?? 0)) || 0);
    return getEstimateNumberValueBasedOnSettings(val, estimateData, "amount");
  };

  return (
    <div className="flex flex-col gap-6 py-6 w-full">
      <div className="flex items-center gap-4">
        <div className="flex flex-1 items-center text-base font-semibold gap-2">
          <div>
            <span>{t("Amount")}</span>
            <span>({currencySymbol})</span>
          </div>
          <IconComponent
            name="details"
            position="bottom"
            toolTipContent={t("Amount = Quantity * Rate")}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
        <div className="flex flex-4">
          <div className="flex gap-10">
            <TextFieldComponent
              width="18.75rem"
              value={getEstimateNumberValueBasedOnSettings(itemCurrentDetails.amount ?? 0, estimateData, "amount")}
              disabled
            />
            <div className="flex gap-2 items-center">
              <span>(</span>
              <span>{t("Quantity")}</span>
              <div className="px-4 text-sm font-semibold py-1 rounded-2xl border border-text_light">
                {getQuantity()}
              </div>
              <span>×</span>
              <div>
                <span>{t("Rate")}</span>({currencySymbol})
              </div>
              <div className="px-4 text-sm py-1 bg-surface_neutral rounded">{itemCurrentDetails.rate ?? 0}</div>
              <span>)</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center flex-1 text-base font-semibold">
          <span>{t("Additional Amount")}</span>
          <span>({currencySymbol})</span>
        </div>
        <div className="flex flex-4">
          <NumericTextBoxComponent
            width="18.75rem"
            showSpinButton={false}
            value={itemCurrentDetails.additionalAmount ? Number(itemCurrentDetails.additionalAmount) : undefined}
            onChange={(args: UpdateItemDetailsArgs) => updateItemDetails(args, "additionalAmount")}
            disabled={!isActionPermission}
          />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center flex-1 text-base font-semibold gap-2">
          <div>
            <span>{t("Total Amount")}</span>
            <span>({currencySymbol})</span>
          </div>
          <IconComponent
            name="details"
            position="bottom"
            toolTipContent={t("Total Amount = Amount + Additional Amount")}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
        <div className="flex flex-4">
          <TextFieldComponent width="18.75rem" value={getTotalAmount()} disabled />
        </div>
      </div>
    </div>
  );
};

export default BoqItemAmountDetails;
